/**
 * Number Formatting Utilities
 * Provides consistent number formatting across all dashboards
 */

export interface FormatNumberOptions {
  /**
   * Number of decimal places (default: 0 for integers, 2 for floats)
   */
  decimals?: number;
  /**
   * Whether to show decimals even for whole numbers (default: false)
   */
  forceDecimals?: boolean;
  /**
   * Custom suffix (e.g., 'M' for millions, 'K' for thousands)
   */
  suffix?: string;
  /**
   * Minimum value to show suffix (e.g., 1000 for 'K', 1000000 for 'M')
   */
  suffixThreshold?: number;
  /**
   * Whether to use compact notation (1.2M instead of 1,200,000)
   */
  compact?: boolean;
}

/**
 * Format a number with thousand separators and optional decimal places
 * 
 * @example
 * formatNumber(1234567) // "1,234,567"
 * formatNumber(1234.56) // "1,234.56"
 * formatNumber(1234.5, { decimals: 2 }) // "1,234.50"
 * formatNumber(1500000, { compact: true }) // "1.5M"
 */
export function formatNumber(
  value: number | string | null | undefined,
  options: FormatNumberOptions = {}
): string {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    return '0';
  }

  const {
    decimals,
    forceDecimals = false,
    suffix,
    suffixThreshold,
    compact = false,
  } = options;

  // Determine decimal places
  let decimalPlaces: number;
  if (decimals !== undefined) {
    decimalPlaces = decimals;
  } else {
    // Auto-detect: use 2 decimals for floats, 0 for integers
    decimalPlaces = Number.isInteger(num) ? 0 : 2;
  }

  // Compact notation (1.2M, 1.5K, etc.)
  if (compact) {
    const absNum = Math.abs(num);
    let compactValue: number;
    let compactSuffix: string;

    if (absNum >= 1_000_000_000) {
      compactValue = num / 1_000_000_000;
      compactSuffix = 'B';
    } else if (absNum >= 1_000_000) {
      compactValue = num / 1_000_000;
      compactSuffix = 'M';
    } else if (absNum >= 1_000) {
      compactValue = num / 1_000;
      compactSuffix = 'K';
    } else {
      compactValue = num;
      compactSuffix = '';
    }

    // Format with appropriate decimals
    const formatted = compactValue.toLocaleString('en-US', {
      minimumFractionDigits: forceDecimals ? decimalPlaces : 0,
      maximumFractionDigits: decimalPlaces,
    });

    return `${formatted}${compactSuffix}`;
  }

  // Custom suffix with threshold
  if (suffix && suffixThreshold && Math.abs(num) >= suffixThreshold) {
    const divided = num / suffixThreshold;
    const formatted = divided.toLocaleString('en-US', {
      minimumFractionDigits: forceDecimals ? decimalPlaces : 0,
      maximumFractionDigits: decimalPlaces,
    });
    return `${formatted}${suffix}`;
  }

  // Standard formatting with thousand separators
  return num.toLocaleString('en-US', {
    minimumFractionDigits: forceDecimals ? decimalPlaces : 0,
    maximumFractionDigits: decimalPlaces,
  });
}

/**
 * Format currency values
 * 
 * @example
 * formatCurrency(1234567.89) // "$1,234,567.89"
 * formatCurrency(1500000, { compact: true }) // "$1.5M"
 */
export function formatCurrency(
  value: number | string | null | undefined,
  options: FormatNumberOptions & { symbol?: string } = {}
): string {
  const { symbol = '$', ...formatOptions } = options;
  const formatted = formatNumber(value, formatOptions);
  return `${symbol}${formatted}`;
}

/**
 * Format percentage values
 * 
 * @example
 * formatPercentage(85.5) // "85.5%"
 * formatPercentage(85.5, { decimals: 1 }) // "85.5%"
 * formatPercentage(85, { decimals: 1 }) // "85.0%"
 */
export function formatPercentage(
  value: number | string | null | undefined,
  options: Omit<FormatNumberOptions, 'suffix' | 'suffixThreshold' | 'compact'> = {}
): string {
  const { decimals = 1, forceDecimals = false } = options;
  const formatted = formatNumber(value, {
    decimals,
    forceDecimals,
  });
  return `${formatted}%`;
}

/**
 * Format file sizes
 * 
 * @example
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1048576) // "1 MB"
 * formatFileSize(1073741824) // "1 GB"
 */
export function formatFileSize(
  bytes: number | string | null | undefined,
  options: { decimals?: number } = {}
): string {
  if (bytes === null || bytes === undefined || bytes === '') {
    return '0 B';
  }

  const num = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
  
  if (isNaN(num) || num === 0) {
    return '0 B';
  }

  const { decimals = 1 } = options;
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(Math.abs(num)) / Math.log(k));
  const size = num / Math.pow(k, i);

  return `${formatNumber(size, { decimals, forceDecimals: false })} ${sizes[i]}`;
}

